/**
 * ReportWeeklyPage - Página de Reporte Semanal
 * Muestra depósitos en un rango de fechas con totales y agrupaciones
 */
import { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { PageHeader, LoadingSpinner } from '../components/common';
import { useNotification } from '../context/NotificationContext';
import reporteService from '../services/reporteService';
import { parseApiDate } from '../utils/formatters';

const ReportWeeklyPage = () => {
  // Calcular fecha de inicio de la semana actual (lunes)
  const getStartOfWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(getStartOfWeek());
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const { showSuccess, showError } = useNotification();

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      const response = await reporteService.getWeeklyReport(startDate, endDate);
      const data = response.data || response;
      setReportData(data);
      showSuccess('Reporte generado correctamente');
    } catch (error) {
      console.error('Error al generar reporte:', error);
      showError('Error al generar el reporte semanal');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const blob = await reporteService.exportReportToExcel('semanal', {
        fecha_inicio: startDate,
        fecha_fin: endDate,
      });
      reporteService.downloadReport(blob, `reporte-semanal-${startDate}-${endDate}.xlsx`);
      showSuccess('Reporte exportado a Excel correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      showError('Error al exportar el reporte a Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const blob = await reporteService.exportReportToPDF('semanal', {
        fecha_inicio: startDate,
        fecha_fin: endDate,
      });
      reporteService.downloadReport(blob, `reporte-semanal-${startDate}-${endDate}.pdf`);
      showSuccess('Reporte exportado a PDF correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      showError('Error al exportar el reporte a PDF');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    const parsed = parseApiDate(dateString);
    return parsed ? parsed.toLocaleDateString('es-AR') : '-';
  };

  if (loading && !reportData) {
    return <LoadingSpinner message="Generando reporte semanal..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Reporte Semanal"
        subtitle="Resumen de depósitos en un rango de fechas"
        breadcrumbs={[
          { label: 'Inicio', to: '/dashboard' },
          { label: 'Reportes' },
          { label: 'Semanal' },
        ]}
      />

      {/* Filtros y Acciones */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              label="Fecha Desde"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Fecha Hasta"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleGenerateReport}
                disabled={loading}
              >
                Generar Reporte
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={!reportData || loading}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportPDF}
                disabled={!reportData || loading}
              >
                Exportar PDF
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {reportData && (
        <>
          {/* Resumen General */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Depósitos
                  </Typography>
                  <Typography variant="h4">
                    {reportData.totales?.cantidad_depositos || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {reportData.periodo?.fecha_inicio} - {reportData.periodo?.fecha_fin}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Cheques
                  </Typography>
                  <Typography variant="h4">
                    {reportData.totales?.cantidad_cheques || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Monto Total
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(reportData.totales?.monto_total)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Resumen por Banco */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Resumen por Banco
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Banco</TableCell>
                    <TableCell align="right"># Depósitos</TableCell>
                    <TableCell align="right"># Cheques</TableCell>
                    <TableCell align="right">Monto Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.resumen_por_banco?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No hay datos disponibles
                      </TableCell>
                    </TableRow>
                  ) : (
                    reportData.resumen_por_banco?.map((banco, index) => (
                      <TableRow key={index}>
                        <TableCell>{banco.banco}</TableCell>
                        <TableCell align="right">{banco.cantidad_depositos}</TableCell>
                        <TableCell align="right">{banco.cantidad_cheques}</TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(banco.monto_total)}</strong>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Resumen por Fecha */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Resumen por Fecha
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {reportData.resumen_por_fecha?.length === 0 ? (
              <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
                No hay depósitos en el rango de fechas seleccionado
              </Typography>
            ) : (
              reportData.resumen_por_fecha?.map((diaData, index) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                      <Typography>
                        <strong>{formatDate(diaData.fecha)}</strong>
                      </Typography>
                      <Typography color="textSecondary">
                        {diaData.cantidad_depositos} depósito(s) - {diaData.cantidad_cheques} cheque(s) - {formatCurrency(diaData.monto_total)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Cuenta Bancaria</TableCell>
                            <TableCell>Banco</TableCell>
                            <TableCell align="right"># Cheques</TableCell>
                            <TableCell align="right">Monto</TableCell>
                            <TableCell>Nº Boleta</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {diaData.depositos?.map((deposito) => (
                            <TableRow key={deposito.id}>
                              <TableCell>
                                {deposito.cuenta_bancaria?.numero_cuenta || '-'}
                              </TableCell>
                              <TableCell>
                                {deposito.cuenta_bancaria?.banco?.nombre || deposito.banco_destino?.nombre || '-'}
                              </TableCell>
                              <TableCell align="right">
                                {deposito.cheques?.length || 0}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(deposito.monto_total)}
                              </TableCell>
                              <TableCell>
                                {deposito.numero_boleta || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Paper>
        </>
      )}

      {!reportData && !loading && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Seleccione un rango de fechas y haga clic en "Generar Reporte" para ver los datos
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ReportWeeklyPage;
